import React, { FC, useEffect, useMemo, useState, startTransition } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Folder as FolderIcon,
  LocationOn as LocationOnIcon,
  Wifi as WifiIcon,
  ArrowForward as ArrowForwardIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useAppState } from 'src/state-management/state-provider';
import { useNavigate } from 'react-router-dom';
import clientService from 'src/clientService';
import { DeploymentStatus, DeviceStatus } from 'src/api/Api';
import recentRequests from 'src/resources/mock/recent_requests.json';
import { formatDate } from 'src/util/date-utils';

const StatCard: FC<{ icon: React.ElementType, label: string, value: string | number, color: string }> = ({ icon: Icon, label, value, color }) => (
  <Card sx={{
    bgcolor: '#111',
    border: '1px solid #222',
    borderRadius: '16px',
    height: '100%',
    transition: 'transform 0.2s',
    '&:hover': { transform: 'translateY(-4px)', borderColor: '#333' }
  }}>
    <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2.5 }}>
      <Box sx={{
        p: 1.5,
        borderRadius: '12px',
        bgcolor: `${color}15`,
        color: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Icon sx={{ fontSize: 28 }} />
      </Box>
      <Box>
        <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </Typography>
        <Typography variant="h3" sx={{ color: '#FFF', fontWeight: 800 }}>
          {value}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const DonutChart: FC<{
  title: string,
  total: number,
  data: { name: string, y: number, color: string }[],
  centerLabel: string,
  onViewAll?: () => void
}> = ({ title, total, data, centerLabel, onViewAll }) => {
  const options: Highcharts.Options = {
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      height: 280,
    },
    title: {
      text: `<div style="text-align: center"><span style="font-size: 24px; color: #FFF; font-weight: 800">${total}</span><br/><span style="font-size: 12px; color: #666; font-weight: 600; text-transform: uppercase">${centerLabel}</span></div>`,
      align: 'center',
      verticalAlign: 'middle',
      y: 10,
      useHTML: true
    },
    tooltip: {
      pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
    },
    plotOptions: {
      pie: {
        innerSize: '80%',
        borderWidth: 0,
        dataLabels: {
          enabled: false
        },
        showInLegend: false
      }
    },
    series: [{
      type: 'pie',
      name: title,
      data: data
    }],
    credits: { enabled: false }
  };

  return (
    <Card sx={{ bgcolor: '#111', border: '1px solid #222', borderRadius: '24px', height: '100%' }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h6" sx={{ color: '#FFF', fontWeight: 700 }}>{title}</Typography>
          <Box
            component="span"
            onClick={onViewAll}
            sx={{
              color: '#10B981',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { opacity: 0.8 }
            }}
          >
            VIEW ALL <ArrowForwardIcon sx={{ fontSize: 14 }} />
          </Box>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <HighchartsReact highcharts={Highcharts} options={options} />
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {data.map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
                    <Typography sx={{ color: '#FFF', fontSize: '14px', fontWeight: 500 }}>{item.name}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography component="span" sx={{ color: '#FFF', fontSize: '14px', fontWeight: 700, mr: 0.5 }}>
                      {item.y}
                    </Typography>
                    <Typography component="span" sx={{ color: '#666', fontSize: '12px', fontWeight: 500 }}>
                      ({((item.y / total) * 100 || 0).toFixed(1)}%)
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const AdminOverview: FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [projectsRes, deploymentsRes, devicesRes] = await Promise.all([
          clientService.api.getAllProjectsAdminApiV1AdminProjectsGet(),
          clientService.api.getAllDeploymentsApiV1AdminDeploymentsGet(),
          clientService.api.getAllDevicesAdminApiV1AdminDevicesGet()
        ]);

        startTransition(() => {
          dispatch({ type: 'ADMIN_SET_PROJECTS', payload: projectsRes.data });
          dispatch({ type: 'ADMIN_SET_DEPLOYMENT_LIST', payload: Array.isArray(deploymentsRes.data) ? (deploymentsRes.data as any) : ((deploymentsRes.data as any)?.deployments || []) });
          dispatch({ type: 'ADMIN_SET_DEVICES', payload: devicesRes.data });
          setLoading(false);
        });
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  const stats = useMemo(() => {
    const projects = state.admin.projects;
    const deployments = state.admin.deployments;

    const totalOrgs = new Set(projects.map(p => p.project_developer).filter(Boolean)).size;
    const totalProjects = projects.length;
    const totalSites = projects.reduce((acc, curr) => acc + (curr.assignment?.project?.site_count || 0), 0);
    const liveDeployments = deployments.filter(d => d.status === DeploymentStatus.Ongoing).length;

    return { totalOrgs, totalProjects, totalSites, liveDeployments };
  }, [state.admin.projects, state.admin.deployments]);

  const deploymentData = useMemo(() => {
    const deployments = state.admin.deployments;
    const upcoming = deployments.filter(d => d.status === DeploymentStatus.Upcoming).length;
    const ongoing = deployments.filter(d => d.status === DeploymentStatus.Ongoing).length;
    const past = deployments.filter(d => d.status === DeploymentStatus.Past).length;

    return [
      { name: 'Upcoming', y: upcoming, color: '#F59E0B' },
      { name: 'Ongoing', y: ongoing, color: '#10B981' },
      { name: 'Completed', y: past, color: '#3B82F6' },
    ];
  }, [state.admin.deployments]);

  const deviceData = useMemo(() => {
    const devices = state.admin.devices;
    const active = devices.filter(d => d.status === DeviceStatus.ACTIVE).length;
    const inactive = devices.filter(d => d.status === DeviceStatus.INACTIVE).length;
    const retired = devices.filter(d => d.status === DeviceStatus.RETIRED).length;

    return [
      { name: 'Online', y: active, color: '#10B981' },
      { name: 'Offline', y: inactive, color: '#EF4444' },
      { name: 'Inactive', y: retired, color: '#6B7280' },
    ];
  }, [state.admin.devices]);


  // No Recent Requests API exists — showing N/A placeholder


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#0A0A0A' }}>
        <CircularProgress sx={{ color: '#10B981' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 4, py: 4, backgroundColor: '#0A0A0A', minHeight: '100vh', color: '#FFFFFF' }}>
      <Box sx={{ mb: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.5px' }}>Overview</Typography>
          <Typography sx={{ color: '#666', fontSize: '14px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>
            Real-time environmental intelligence and operations
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value="All Organisations"
            sx={{
              bgcolor: '#111',
              color: '#FFF',
              borderRadius: '12px',
              border: '1px solid #222',
              fontSize: '14px',
              fontWeight: 600,
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '& .MuiSelect-icon': { color: '#666' }
            }}
          >
            <MenuItem value="All Organisations">All Organisations</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Top Stats */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={BusinessIcon} label="Total Organisations" value={stats.totalOrgs} color="#10B981" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={FolderIcon} label="Total Projects" value={stats.totalProjects} color="#3B82F6" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={LocationOnIcon} label="Total Sites Monitoring" value={stats.totalSites} color="#8B5CF6" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={WifiIcon} label="Live Deployments" value={stats.liveDeployments} color="#EAB308" />
        </Grid>
      </Grid>

      {/* Middle Status Charts */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={6}>
          <DonutChart
            title="Deployment Status"
            total={state.admin.deployments.length}
            data={deploymentData}
            centerLabel="Total Deployments"
            onViewAll={() => startTransition(() => navigate('/admin/deployments'))}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DonutChart
            title="Device Status"
            total={state.admin.devices.length}
            data={deviceData}
            centerLabel="Total Devices"
            onViewAll={() => startTransition(() => navigate('/admin/devices'))}
          />
        </Grid>
      </Grid>

      {/* Recent Requests Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Recent Requests</Typography>
        <Box
          component="span"
          sx={{
            color: '#10B981',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            '&:hover': { opacity: 0.8 }
          }}
        >
          VIEW ALL REQUESTS <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>

      {/* Table-style Headers */}
      <Box sx={{ px: 2, mb: 2, display: 'grid', gridTemplateColumns: '1.2fr 1.8fr 2fr 2.5fr 1fr 1.2fr 1.8fr 40px', gap: 2 }}>
        {['REQUEST ID', 'REQUESTER', 'ORGANISATION', 'SUBJECT', 'PRIORITY', 'STATUS', 'CREATED AT'].map((header) => (
          <Typography key={header} variant="caption" sx={{ color: '#666', fontWeight: 700, letterSpacing: '1px' }}>
            {header}
          </Typography>
        ))}
        <Box />
      </Box>

      {/* Request Rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {recentRequests.map((req) => (
          <Box
            key={req.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 1.8fr 2fr 2.5fr 1fr 1.2fr 1.8fr 40px',
              gap: 2,
              alignItems: 'center',
              px: 2,
              py: 2,
              bgcolor: '#111',
              border: '1px solid #222',
              borderRadius: '12px',
              transition: 'all 0.2s',
              cursor: 'pointer',
              '&:hover': {
                borderColor: '#444',
                bgcolor: '#151515',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Typography sx={{ color: '#FFF', fontSize: '13px', fontWeight: 600 }}>{req.id}</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: `${req.color}20`,
                  color: req.color,
                  fontSize: '12px',
                  fontWeight: 700,
                  border: `1px solid ${req.color}40`
                }}
              >
                {req.requester.split(' ').map((n: string) => n[0]).join('')}
              </Avatar>
              <Typography sx={{ color: '#FFF', fontSize: '13px', fontWeight: 500 }}>{req.requester}</Typography>
            </Box>

            <Typography sx={{ color: '#AAA', fontSize: '13px', fontWeight: 500 }}>{req.organisation}</Typography>

            <Typography sx={{ color: '#DDD', fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {req.subject}
            </Typography>

            <Box>
              <Box sx={{
                display: 'inline-block',
                px: 1.5,
                py: 0.5,
                borderRadius: '6px',
                bgcolor: req.priority === 'High' ? '#EF444415' : '#F59E0B15',
                color: req.priority === 'High' ? '#EF4444' : '#F59E0B',
                fontSize: '11px',
                fontWeight: 800,
                textTransform: 'uppercase'
              }}>
                {req.priority}
              </Box>
            </Box>

            <Box>
              <Box sx={{
                display: 'inline-block',
                px: 1.5,
                py: 0.5,
                borderRadius: '6px',
                bgcolor: req.status === 'Open' ? '#10B98115' : req.status === 'In Progress' ? '#3B82F615' : '#6B728015',
                color: req.status === 'Open' ? '#10B981' : req.status === 'In Progress' ? '#3B82F6' : '#9CA3AF',
                fontSize: '11px',
                fontWeight: 800,
                whiteSpace: 'nowrap'
              }}>
                {req.status}
              </Box>
            </Box>

            <Typography sx={{ color: '#666', fontSize: '13px', fontWeight: 500 }}>
              {formatDate(req.created_at, 'd MMM yyyy, hh:mm a')}
            </Typography>

            <IconButton size="small" sx={{ color: '#555', '&:hover': { color: '#FFF' } }}>
              <MoreVertIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AdminOverview;
